import React, { useRef, useEffect, useState } from 'react'
import { useAppSelector } from '../../../../app/hooks'
import LinkButton from './LinkButton'
import { selectedLmsAdminSearch, selectedLmsStatus } from '../../lmsSlice'
import { AdminTableProps } from '../../models'
import ProvenanceView from '../../../../components/ProvenanceView'
import { IconButton } from '@mui/material'
import { SUPER_ADMIN_DELETE } from '../../../../utils/roles'
import KeycloakService from '../../../../utils/keycloakService'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import {
  create,
  update,
  departmentTableHeadings,
  menuValues,
} from '../../constants'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined'
import '../../../administration/admin.scss'
import { DeleteConfirmationDialog } from '../admin/DeleteConfirmationDialog'
import { Archive } from '../admin/Archive'
import { activeText } from '../../../Radiology/Constants/constants'

const DepartmentTable = ({ data, onEdit }: AdminTableProps) => {
  const searchParam = useAppSelector(selectedLmsAdminSearch)
  const status = useAppSelector(selectedLmsStatus)
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false)
  const [tableData, setTableData] = useState<any>(data)
  const [departmentData, setDepartmentData] = useState<any>({})
  const filtered = tableData?.filter((d: any) => {
    const name = d.department_name?.toLowerCase()?.includes(searchParam)
    const departmentStatus = d?.status === status
    return name && departmentStatus
  })

  filtered?.sort?.(
    (a: { department_name: string }, b: { department_name: string }) =>
      a.department_name?.toLowerCase() < b.department_name?.toLowerCase()
        ? -1
        : 1
  )

  const depTableContainerRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const resizeHandler = () => {
      const container = depTableContainerRef.current
      if (container) {
        const availableHeight: any =
          window.innerHeight - container.getBoundingClientRect().top
        container.style.maxHeight = `${availableHeight - 20}px`
      }
    }

    window.addEventListener('resize', resizeHandler)
    resizeHandler()

    return () => {
      window.removeEventListener('resize', resizeHandler)
    }
  }, [])

  useEffect(() => {
    setTableData(data)
  }, [data])

  function handleDeleteDialogOpen(d: any) {
    setDeleteDialog(true)
    setDepartmentData(d)
  }

  function handleCloseDeleteDialog() {
    setDeleteDialog(false)
    setDepartmentData(null)
  }

  return (
    <div
      className='data-table table-fixed admin-table-table-container'
      ref={depTableContainerRef}
      style={{ overflowY: 'auto' }}
    >
      <table className='data-table table-fixed admin-table relative'>
        <thead style={{ position: 'sticky', top: '0px' }}>
          <tr>
            <th className='table-w-5'>{departmentTableHeadings?.id}</th>
            <th className='table-w-15'>
              {departmentTableHeadings?.department}
            </th>
            <th>{departmentTableHeadings?.description}</th>
            <th className='table-w-10'>{departmentTableHeadings?.createdBy}</th>
            <th className='table-w-10'>{departmentTableHeadings?.updatedBy}</th>
            <th className='table-w-8'>{departmentTableHeadings?.actions}</th>
          </tr>
        </thead>
        <tbody>
          {filtered?.length ? (
            filtered?.map((d: any) => (
              <tr key={d?.id}>
                <td>{d?.id}</td>
                <td>
                  <LinkButton
                    id={d.id}
                    name={d.department_name}
                    onClick={onEdit}
                  />
                </td>
                <td>{d?.department_description ?? '-'}</td>
                <td>
                  <ProvenanceView
                    mode={create}
                    date={d?.created_date ?? ''}
                    name={d?.created_by ?? ''}
                  />
                </td>
                <td>
                  <ProvenanceView
                    mode={update}
                    date={d?.updated_date ?? ''}
                    name={d?.updated_by ?? ''}
                  />
                </td>
                <td>
                  <div className='set-action-icons'>
                    {d?.status === activeText ? (
                      <IconButton
                        title='Archive'
                        size='small'
                        onClick={() =>
                          Archive(d, setTableData, menuValues?.LabDepartment)
                        }
                        disabled={
                          !KeycloakService?.hasRole([SUPER_ADMIN_DELETE])
                        }
                      >
                        <ArchiveOutlinedIcon fontSize='small' />
                      </IconButton>
                    ) : (
                      <IconButton
                        title='un-Archive'
                        size='small'
                        onClick={() =>
                          Archive(d, setTableData, menuValues?.LabDepartment)
                        }
                        disabled={
                          !KeycloakService?.hasRole([SUPER_ADMIN_DELETE])
                        }
                      >
                        <UnarchiveOutlinedIcon fontSize='small' />
                      </IconButton>
                    )}
                    <IconButton
                      title='Delete'
                      size='small'
                      onClick={() => handleDeleteDialogOpen(d)}
                      disabled={!KeycloakService?.hasRole([SUPER_ADMIN_DELETE])}
                    >
                      <DeleteOutlined fontSize='small' />
                    </IconButton>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={3} className='text-center'>
                No Departments Found
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {deleteDialog && (
        <DeleteConfirmationDialog
          openDialog={deleteDialog}
          closeDialog={handleCloseDeleteDialog}
          data={departmentData}
          name={departmentData?.department_name}
          setData={setTableData}
          item={menuValues?.LabDepartment}
        />
      )}
    </div>
  )
}

export default DepartmentTable
